import { graphHooks } from '@tunasong/graph-lib/react'
import { MoreVertIcon } from '@tunasong/icons'
import type { Comment, Persisted } from '@tunasong/schemas'
import { isAgentAuthoredComment } from '@tunasong/schemas'
import { IconButton, Menu, MenuContent, MenuItem, MenuTrigger } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useChatActions } from './chat-actions.hook.js'
import { useChatToast } from './chat-toast.js'
import { taskFailedToast } from './task-failed.js'
import styles from './comment-menu.module.css'

/** T3 to T6 (chat tasks, GH #3041) */
const makeTaskLabel = 'Make it a task'
const notTaskLabel = 'Not a task'
const markedText = 'Marked as a task.'
const unmarkedText = 'No longer a task.'

/** Each row's menu is distinct in a screen reader's list of buttons, after L1 (Brian, 2026-11-02) */
const commentMenuName = (author: string, isTask: boolean) =>
  `More actions for ${author}'s ${isTask ? 'task' : 'comment'}`

export interface CommentMenuProps {
  comment: Persisted<Comment>
}

/**
 * A comment's More menu (⋮): "Make it a task", or "Not a task" on a task (chat tasks M2, GH #3041). Anyone who can
 * comment can do either; both act at once and offer Undo in the chat's toast. Undo of "Not a task" asks the server to
 * restore the task as it was, its done state included, so no one can write "Done by" someone else. An agent's comment
 * has no menu.
 */
export const CommentMenu: FC<CommentMenuProps> = props => {
  const { comment } = props
  const { makeTask, notTask, restoreTask } = useChatActions()
  const showToast = useChatToast()
  const { profile } = graphHooks.useProfile(comment.userId)

  if (isAgentAuthoredComment(comment)) {
    return null
  }

  const handleMakeTask = async () => {
    if (await makeTask(comment)) {
      showToast({ message: markedText, actionLabel: 'Undo', onAction: () => void notTask(comment) })
      return
    }
    showToast(taskFailedToast(() => void handleMakeTask()))
  }

  const handleNotTask = async () => {
    if (await notTask(comment)) {
      showToast({ message: unmarkedText, actionLabel: 'Undo', onAction: () => void restoreTask(comment) })
      return
    }
    showToast(taskFailedToast(() => void handleNotTask()))
  }

  return (
    <Menu>
      <MenuTrigger
        render={
          <IconButton
            size="small"
            className={styles.trigger}
            aria-label={commentMenuName(profile?.name ?? 'Unknown', Boolean(comment.task))}
          >
            <MoreVertIcon />
          </IconButton>
        }
      />
      <MenuContent>
        {comment.task ? (
          <MenuItem onClick={() => void handleNotTask()}>{notTaskLabel}</MenuItem>
        ) : (
          <MenuItem onClick={() => void handleMakeTask()}>{makeTaskLabel}</MenuItem>
        )}
      </MenuContent>
    </Menu>
  )
}
